import {
  GetKgInsightSubscriptionResponse,
  PostKgInsightSubscriptionRequest,
} from '@api/api/event-alerts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postKgInsightSubscription } from '@terminal/services/alerts/event-alerts-service';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { optimisticQueriesUpdate } from '@terminal/utils/optimistic-updates/optimistic-updates';

import {
  EventAlertsPaginatedResponse,
  getKgInsightSubscriptionQueryKey,
  QUERY_KEY_EVENT_ALERTS,
} from './event-alerts-query-keys';
import { updateKgInsightAlertTrackingStatus } from './usePostKgInsightSubscription.utils';

export const usePostKgInsightSubscription = () => {
  const queryClient = useQueryClient();
  const isEventAlertsV2Enabled = useFlags(state =>
    state.getFeatureFlagState('enable_event_alerts_v2')
  );

  return useMutation({
    mutationFn: (body: PostKgInsightSubscriptionRequest) => {
      if (!isEventAlertsV2Enabled) {
        return Promise.resolve();
      }
      return postKgInsightSubscription(body);
    },
    onMutate: async (body: PostKgInsightSubscriptionRequest) => {
      if (!isEventAlertsV2Enabled) {
        return;
      }

      const subscriptionQueryKey = getKgInsightSubscriptionQueryKey(
        body.entity_tag
      );
      const trackingStatus = body.track ? 'active' : 'stopped';

      await queryClient.cancelQueries({ queryKey: subscriptionQueryKey });
      const previousSubscriptionQuery =
        queryClient.getQueryData<GetKgInsightSubscriptionResponse>(
          subscriptionQueryKey
        );

      queryClient.setQueryData<GetKgInsightSubscriptionResponse>(
        subscriptionQueryKey,
        {
          entity_tag: body.entity_tag,
          source_type: 'kg-insight',
          category: body.track
            ? body.category
            : previousSubscriptionQuery?.category,
          tracking_status: trackingStatus,
        }
      );

      const { previousData: previousAlertQueries } =
        await optimisticQueriesUpdate<EventAlertsPaginatedResponse>({
          queryClient,
          queryKey: [QUERY_KEY_EVENT_ALERTS],
          updateFn: data =>
            updateKgInsightAlertTrackingStatus({
              data,
              entityTag: body.entity_tag,
              trackingStatus,
            }),
        });

      return {
        previousAlertQueries,
        previousSubscriptionQuery,
        subscriptionQueryKey,
      };
    },
    onError: (_error, _body, context) => {
      if (context?.previousSubscriptionQuery) {
        queryClient.setQueryData(
          context.subscriptionQueryKey,
          context.previousSubscriptionQuery
        );
      } else if (context?.subscriptionQueryKey) {
        queryClient.removeQueries({
          queryKey: context.subscriptionQueryKey,
          exact: true,
        });
      }

      context?.previousAlertQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: (_data, _error, body) => {
      if (!isEventAlertsV2Enabled) {
        return;
      }

      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: getKgInsightSubscriptionQueryKey(body.entity_tag),
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_EVENT_ALERTS],
        }),
      ]);
    },
  });
};
